import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, ShieldCheck, ArrowLeft, CheckCircle2 } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input, Label } from "../components/ui/input";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setResult(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <div className="w-full max-w-md card-surface p-8">
        <div className="flex items-center gap-2 mb-6">
          <div className="w-9 h-9 rounded-md bg-primary text-primary-foreground flex items-center justify-center">
            <ShieldCheck className="w-5 h-5" />
          </div>
          <span className="text-lg font-semibold">CCAMS</span>
        </div>

        <h1 className="text-2xl font-semibold tracking-tight">Forgot your password?</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Enter your work email and we&apos;ll generate a password reset link.
        </p>

        {!result && (
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <div className="mt-1.5 relative">
                <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="email"
                  data-testid="forgot-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-9"
                  required
                />
              </div>
            </div>
            {error && (
              <div className="text-sm text-red-600" data-testid="forgot-error">
                {error}
              </div>
            )}
            <Button type="submit" data-testid="forgot-submit" disabled={loading} className="w-full">
              {loading ? "Generating link…" : "Send reset link"}
            </Button>
          </form>
        )}

        {result && (
          <div className="mt-6 space-y-4" data-testid="forgot-result">
            <div className="flex items-start gap-2 rounded-md border border-green-200 bg-green-50 text-green-800 px-3 py-2 text-sm dark:border-green-900/50 dark:bg-green-950/40 dark:text-green-300">
              <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{result.message}</span>
            </div>
            {result.reset_link && (
              <div className="rounded-md border border-border bg-muted p-3 text-xs">
                <div className="label-eyebrow mb-1">Reset link (mocked email)</div>
                <Link
                  to={result.reset_link}
                  data-testid="forgot-reset-link"
                  className="text-primary break-all hover:underline"
                >
                  {result.reset_link}
                </Link>
              </div>
            )}
          </div>
        )}

        <Link
          to="/login"
          data-testid="forgot-back-login"
          className="mt-6 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to sign in
        </Link>
      </div>
    </div>
  );
}
